import { useState } from "react";
import { api, inr, nfmt, fmtDate, downloadCSV } from "./utils";
import { DownloadSimple, Play } from "@phosphor-icons/react";

const ENTITIES = [
  { key: "businesses", label: "Businesses" },
  { key: "customers", label: "End Customers" },
  { key: "invoices", label: "Invoices" },
  { key: "payments", label: "Payments" },
  { key: "reminders", label: "Reminders" },
];

export default function AdminReports() {
  const [entity, setEntity] = useState("businesses");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [filters, setFilters] = useState({});
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const run = async () => {
    setLoading(true); setErr("");
    try {
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== "" && v !== null && v !== undefined)
      );
      const body = { entity, filters: cleanFilters, limit: 500, ...(start && end ? { start, end } : {}) };
      const { data } = await api.post("/admin/reports", body);
      setRows(data.rows || []); setCount(data.count || 0);
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const exportCSV = () => downloadCSV(entity, { start, end, user_id: filters.user_id });

  return (
    <div className="space-y-4" data-testid="admin-reports">
      <div className="nb-card p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-[10px] font-bold uppercase tracking-wider">Entity</label>
          <select data-testid="report-entity" value={entity} onChange={(e) => { setEntity(e.target.value); setFilters({}); setRows([]); }} className="nb-input text-sm px-2 py-2">
            {ENTITIES.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
          </select>
          <label className="text-[10px] font-bold uppercase tracking-wider ml-2">From</label>
          <input data-testid="report-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="nb-input text-sm px-2 py-2" />
          <label className="text-[10px] font-bold uppercase tracking-wider">To</label>
          <input data-testid="report-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="nb-input text-sm px-2 py-2" />
        </div>

        {/* Entity-specific filters */}
        <div className="flex flex-wrap items-center gap-2">
          {entity === "businesses" && (
            <>
              <input data-testid="f-min-invoices" type="number" placeholder="Min invoices" value={filters.min_invoices || ""} onChange={(e) => setF("min_invoices", e.target.value)} className="nb-input text-sm px-2 py-2 w-36" />
              <input data-testid="f-min-outstanding" type="number" placeholder="Min outstanding ₹" value={filters.min_outstanding || ""} onChange={(e) => setF("min_outstanding", e.target.value)} className="nb-input text-sm px-2 py-2 w-44" />
              <input data-testid="f-min-customers" type="number" placeholder="Min customers" value={filters.min_customers || ""} onChange={(e) => setF("min_customers", e.target.value)} className="nb-input text-sm px-2 py-2 w-40" />
              <input data-testid="f-inactive-days" type="number" placeholder="Inactive N days" value={filters.inactive_days || ""} onChange={(e) => setF("inactive_days", e.target.value)} className="nb-input text-sm px-2 py-2 w-40" />
            </>
          )}
          {(entity === "customers" || entity === "invoices" || entity === "payments" || entity === "reminders") && (
            <input data-testid="f-user-id" placeholder="Filter by user_id (optional)" value={filters.user_id || ""} onChange={(e) => setF("user_id", e.target.value)} className="nb-input text-sm px-2 py-2 w-72" />
          )}
          {entity === "invoices" && (
            <>
              <select data-testid="f-inv-status" value={filters.status || ""} onChange={(e) => setF("status", e.target.value)} className="nb-input text-sm px-2 py-2">
                <option value="">Any status</option>
                <option value="paid">Paid</option><option value="pending">Pending</option>
                <option value="partially_paid">Partially paid</option><option value="overdue">Overdue</option>
              </select>
              <input data-testid="f-inv-min" type="number" placeholder="Min amount" value={filters.min_amount || ""} onChange={(e) => setF("min_amount", e.target.value)} className="nb-input text-sm px-2 py-2 w-32" />
            </>
          )}
          {entity === "payments" && (
            <select data-testid="f-pay-method" value={filters.method || ""} onChange={(e) => setF("method", e.target.value)} className="nb-input text-sm px-2 py-2">
              <option value="">Any method</option>
              <option value="cash">Cash</option><option value="upi">UPI</option>
              <option value="bank">Bank</option><option value="cheque">Cheque</option>
              <option value="card">Card</option><option value="other">Other</option>
            </select>
          )}
          {entity === "reminders" && (
            <>
              <select data-testid="f-rem-channel" value={filters.channel || ""} onChange={(e) => setF("channel", e.target.value)} className="nb-input text-sm px-2 py-2">
                <option value="">Any channel</option><option value="whatsapp">WhatsApp</option>
              </select>
              <select data-testid="f-rem-status" value={filters.status || ""} onChange={(e) => setF("status", e.target.value)} className="nb-input text-sm px-2 py-2">
                <option value="">Any status</option><option value="sent">Sent</option><option value="failed">Failed</option>
              </select>
            </>
          )}
          <button data-testid="run-report" onClick={run} className="nb-btn bg-[#4361EE] text-white text-xs px-3 py-2 inline-flex items-center gap-1">
            <Play size={14} weight="bold" /> Run report
          </button>
          <button data-testid="export-report" onClick={exportCSV} className="nb-btn bg-white text-xs px-3 py-2 inline-flex items-center gap-1">
            <DownloadSimple size={14} weight="bold" /> Export CSV
          </button>
        </div>
      </div>

      {err && <div className="nb-card p-3 bg-[#FF6B6B] text-white text-sm font-bold">{err}</div>}

      <div className="nb-card overflow-x-auto">
        <div className="px-4 py-2 border-b-2 border-black/10 text-xs font-bold uppercase tracking-wider bg-[#F3F0E9]">
          Results: {loading ? "loading…" : `${nfmt(count)} rows`}
        </div>
        <ReportTable entity={entity} rows={rows} />
      </div>
    </div>
  );
}

function ReportTable({ entity, rows }) {
  if (!rows || rows.length === 0) return <div className="p-6 text-center text-sm text-[#4A4A4A]">No data available</div>;
  if (entity === "businesses") return (
    <table className="w-full text-sm"><thead className="bg-[#F3F0E9] text-left"><tr>
      <th className="p-3 font-bold">Business</th><th className="p-3 font-bold">Plan</th>
      <th className="p-3 font-bold text-right">Customers</th><th className="p-3 font-bold text-right">Invoices</th>
      <th className="p-3 font-bold text-right">Invoiced</th><th className="p-3 font-bold text-right">Outstanding</th>
      <th className="p-3 font-bold">Last Active</th></tr></thead>
      <tbody>{rows.map((r) => (<tr key={r.user_id} className="border-t border-black/10">
        <td className="p-3"><div className="font-bold">{r.business_name || r.name}</div><div className="text-[11px] text-[#4A4A4A]">{r.email}</div></td>
        <td className="p-3">{r.plan || "free"}</td>
        <td className="p-3 text-right">{nfmt(r.total_customers)}</td>
        <td className="p-3 text-right">{nfmt(r.total_invoices)}</td>
        <td className="p-3 text-right">{inr(r.total_invoiced)}</td>
        <td className="p-3 text-right">{inr(r.total_outstanding)}</td>
        <td className="p-3 text-[12px]">{fmtDate(r.last_activity)}</td></tr>))}</tbody></table>);
  if (entity === "customers") return (
    <table className="w-full text-sm"><thead className="bg-[#F3F0E9] text-left"><tr>
      <th className="p-3 font-bold">Name</th><th className="p-3 font-bold">Phone</th>
      <th className="p-3 font-bold">Type</th><th className="p-3 font-bold">Next Service</th></tr></thead>
      <tbody>{rows.map((r) => (<tr key={r.customer_id} className="border-t border-black/10">
        <td className="p-3 font-bold">{r.name}</td><td className="p-3">{r.phone}</td>
        <td className="p-3">{r.business_type}</td><td className="p-3">{fmtDate(r.next_service_date)}</td></tr>))}</tbody></table>);
  if (entity === "invoices") return (
    <table className="w-full text-sm"><thead className="bg-[#F3F0E9] text-left"><tr>
      <th className="p-3 font-bold">Invoice #</th><th className="p-3 font-bold">Customer</th>
      <th className="p-3 font-bold text-right">Amount</th><th className="p-3 font-bold text-right">Paid</th>
      <th className="p-3 font-bold text-right">Outstanding</th><th className="p-3 font-bold">Due</th>
      <th className="p-3 font-bold">Status</th></tr></thead>
      <tbody>{rows.map((r) => (<tr key={r.invoice_id} className="border-t border-black/10">
        <td className="p-3 font-bold">{r.invoice_number || "—"}</td>
        <td className="p-3">{r.customer_name}</td>
        <td className="p-3 text-right">{inr(r.amount)}</td>
        <td className="p-3 text-right">{inr(r.amount_paid)}</td>
        <td className="p-3 text-right">{inr(r.outstanding)}</td>
        <td className="p-3">{fmtDate(r.due_date)}</td>
        <td className="p-3">{r.status}</td></tr>))}</tbody></table>);
  if (entity === "payments") return (
    <table className="w-full text-sm"><thead className="bg-[#F3F0E9] text-left"><tr>
      <th className="p-3 font-bold">Date</th><th className="p-3 font-bold">Method</th>
      <th className="p-3 font-bold text-right">Amount</th><th className="p-3 font-bold">Ref</th></tr></thead>
      <tbody>{rows.map((r) => (<tr key={r.payment_id} className="border-t border-black/10">
        <td className="p-3">{fmtDate(r.payment_date)}</td><td className="p-3">{r.method}</td>
        <td className="p-3 text-right font-bold">{inr(r.amount)}</td><td className="p-3">{r.reference || "—"}</td></tr>))}</tbody></table>);
  if (entity === "reminders") return (
    <table className="w-full text-sm"><thead className="bg-[#F3F0E9] text-left"><tr>
      <th className="p-3 font-bold">Sent</th><th className="p-3 font-bold">Customer</th>
      <th className="p-3 font-bold">Channel</th><th className="p-3 font-bold">Status</th></tr></thead>
      <tbody>{rows.map((r) => (<tr key={r.reminder_id} className="border-t border-black/10">
        <td className="p-3">{fmtDate(r.sent_at)}</td><td className="p-3">{r.customer_name}</td>
        <td className="p-3">{r.channel}</td><td className="p-3">{r.status}</td></tr>))}</tbody></table>);
  return <div className="p-6 text-center text-sm text-[#4A4A4A]">No renderer</div>;
}
